import React, { useState, useRef, useEffect } from 'react';
import { View, StyleSheet, Dimensions, Image, Text, Pressable, Linking, Alert } from 'react-native';
import { Gesture, GestureDetector } from 'react-native-gesture-handler';
import Animated, { useAnimatedStyle, useSharedValue, withTiming, runOnJS } from 'react-native-reanimated';
import { useRoute, useNavigation } from '@react-navigation/native';
import { useQuery, gql } from '@apollo/client';
import AsyncStorage from '@react-native-async-storage/async-storage';
import { WebView } from 'react-native-webview';

const INVOICE_CONTENT_QUERY = gql`
  query getInvoiceContent($token: String!, $filename: String!, $dateFrom: String!) {
    invoiceContent(token: $token, filename: $filename, dateFrom: $dateFrom)
  }
`;

const { width: SCREEN_WIDTH, height: SCREEN_HEIGHT } = Dimensions.get('window');

export default function InvoicePreviewScreen() {
  const route = useRoute();
  const { invoiceFile, dateFrom } = route.params;
  const [token, setToken] = useState("");
  const [showPdfInline, setShowPdfInline] = useState(false);
  
  // Get token from AsyncStorage
  useEffect(() => {
    async function fetchToken() {
      try {
        const storedToken = await AsyncStorage.getItem("token");
        setToken(storedToken);
      } catch (ex) {}
    }
    fetchToken();
  }, []);

  // GraphQL query for invoice content
  const { loading, error, data } = useQuery(INVOICE_CONTENT_QUERY, {
    variables: { token, filename: invoiceFile, dateFrom },
    skip: !token, // Don't run query until we have token
  });

  console.log(data)
  // Shared values for animations
  const scale = useSharedValue(1);
  const translateX = useSharedValue(0);
  const translateY = useSharedValue(0);
  const lastScale = useSharedValue(1);
  const lastTranslateX = useSharedValue(0);
  const lastTranslateY = useSharedValue(0);

  // Gesture handlers
  const panGesture = Gesture.Pan()
    .onStart((event) => {
      lastTranslateX.value = translateX.value;
      lastTranslateY.value = translateY.value;
    })
    .onUpdate((event) => {
      if (scale.value > 1) {
        translateX.value = lastTranslateX.value + event.translationX;
        translateY.value = lastTranslateY.value + event.translationY;
      }
    });

  const pinchGesture = Gesture.Pinch()
    .onStart(() => {
      lastScale.value = scale.value;
    })
    .onUpdate((event) => {
      scale.value = Math.max(0.5, Math.min(5, lastScale.value * event.scale));
    })
    .onEnd(() => {
      if (scale.value < 1) {
        scale.value = withTiming(1);
        translateX.value = withTiming(0);
        translateY.value = withTiming(0);
      }
    });

  const composedGesture = Gesture.Simultaneous(panGesture, pinchGesture);

  // Animated styles
  const animatedStyle = useAnimatedStyle(() => {
    'worklet';
    return {
      transform: [
        { scale: scale.value },
        { translateX: translateX.value },
        { translateY: translateY.value },
      ],
    };
  });

  // Determine MIME type based on file extension
  const getMimeType = (filename) => {
    const extension = filename.split('.').pop().toLowerCase();
    switch (extension) {
      case 'pdf':
        return 'application/pdf';
      case 'jpg':
      case 'jpeg':
        return 'image/jpeg';
      case 'png':
        return 'image/png';
      case 'gif':
        return 'image/gif';
      default:
        return 'image/png'; // fallback
    }
  };

  const mimeType = getMimeType(invoiceFile);

  // Function to open PDF with native capabilities
  const openPDF = async () => {
    if (!data?.invoiceContent) return;
    
    try {
      const dataUrl = `data:${mimeType};base64,${data.invoiceContent}`;
      const supported = await Linking.canOpenURL(dataUrl);
      
      if (supported) {
        await Linking.openURL(dataUrl);
      } else {
        Alert.alert(
          'Cannot Open PDF',
          'No PDF viewer found on this device. Please install a PDF viewer app.',
          [{ text: 'OK' }]
        );
      }
    } catch (error) {
      Alert.alert(
        'Error Opening PDF',
        'Failed to open PDF. Please try again.',
        [{ text: 'OK' }]
      );
    }
  };

  // Function to show PDF inline
  const viewPdfInline = () => {
    setShowPdfInline(true);
  };

  // Function to go back to button view
  const backToMenu = () => {
    setShowPdfInline(false);
  };

  return (
    <View style={styles.container}>
      {loading && <Text style={styles.loadingText}>Loading invoice...</Text>}
      {error && <Text style={styles.errorText}>Error loading invoice: {error.message}</Text>}
      {data && data.invoiceContent && (
        <GestureDetector gesture={composedGesture}>
          <View style={styles.imageContainer}>
            <Animated.View style={[styles.imageWrapper, animatedStyle]}>
              {mimeType === 'application/pdf' ? (
                showPdfInline ? (
                  <View style={styles.pdfWebViewContainer}>
                    <View style={styles.pdfHeader}>
                      <Pressable style={styles.backButton} onPress={backToMenu}>
                        <Text style={styles.backButtonText}>← Back</Text>
                      </Pressable>
                      <Text style={styles.pdfTitle}>{invoiceFile}</Text>
                    </View>
                    <WebView
                      source={{ 
                        html: `
                          <html>
                            <head>
                              <meta name="viewport" content="width=device-width, initial-scale=1.0">
                            </head>
                            <body style="margin:0;padding:0;overflow:hidden;">
                              <embed 
                                src="data:${mimeType};base64,${data.invoiceContent}" 
                                width="100%" 
                                height="100%" 
                                type="${mimeType}"
                              />
                            </body>
                          </html>
                        `
                      }}
                      style={styles.webView}
                      javaScriptEnabled={true}
                      domStorageEnabled={true}
                      startInLoadingState={true}
                      scalesPageToFit={true}
                      onError={(syntheticEvent) => {
                        const { nativeEvent } = syntheticEvent;
                        console.warn('WebView error: ', nativeEvent);
                        Alert.alert(
                          'PDF Loading Error',
                          'Unable to display PDF inline. Try opening in external app.',
                          [
                            { text: 'Cancel', style: 'cancel' },
                            { text: 'Open Externally', onPress: openPDF }
                          ]
                        );
                      }}
                      onHttpError={(syntheticEvent) => {
                        const { nativeEvent } = syntheticEvent;
                        console.warn('WebView HTTP error: ', nativeEvent);
                      }}
                    />
                  </View>
                ) : (
                  <View style={styles.pdfContainer}>
                    <Text style={styles.pdfText}>PDF Document</Text>
                    <Text style={styles.pdfSubtext}>File: {invoiceFile}</Text>
                    
                    <View style={styles.buttonContainer}>
                      <Pressable style={styles.primaryButton} onPress={viewPdfInline}>
                        <Text style={styles.primaryButtonText}>View PDF</Text>
                      </Pressable>
                      
                      <Pressable style={styles.secondaryButton} onPress={openPDF}>
                        <Text style={styles.secondaryButtonText}>Open in External App</Text>
                      </Pressable>
                    </View>
                    
                    <Text style={styles.pdfHint}>
                      Choose how you want to view this PDF document
                    </Text>
                  </View>
                )
              ) : (
                <Image
                  source={{ uri: `data:${mimeType};base64,${data.invoiceContent}` }}
                  style={styles.image}
                  resizeMode="contain"
                />
              )}
            </Animated.View>
          </View>
        </GestureDetector>
      )}
    </View>
  );
}

const styles = StyleSheet.create({
  container: {
    flex: 1,
    backgroundColor: '#000',
  },
  imageContainer: {
    flex: 1,
    justifyContent: 'center',
    alignItems: 'center',
  },
  imageWrapper: {
    width: SCREEN_WIDTH,
    height: SCREEN_HEIGHT,
  },
  image: {
    width: '100%',
    height: '100%',
  },
  loadingText: {
    color: 'white',
    fontSize: 18,
    textAlign: 'center',
    marginTop: 50,
  },
  errorText: {
    color: 'red',
    fontSize: 16,
    textAlign: 'center',
    marginTop: 50,
    paddingHorizontal: 20,
  },
  pdfContainer: {
    flex: 1,
    justifyContent: 'center',
    alignItems: 'center',
    backgroundColor: '#f5f5f5',
    padding: 20,
  },
  pdfWebViewContainer: {
    flex: 1,
    backgroundColor: '#fff',
  },
  pdfHeader: {
    flexDirection: 'row',
    alignItems: 'center',
    padding: 15,
    backgroundColor: '#f8f8f8',
    borderBottomWidth: 1,
    borderBottomColor: '#e0e0e0',
  },
  backButton: {
    padding: 8,
  },
  backButtonText: {
    fontSize: 16,
    color: '#007AFF',
    fontWeight: '600',
  },
  pdfTitle: {
    flex: 1,
    fontSize: 16,
    fontWeight: '600',
    color: '#333',
    textAlign: 'center',
    marginRight: 40, // To center despite back button
  },
  webView: {
    flex: 1,
  },
  pdfText: {
    fontSize: 24,
    fontWeight: 'bold',
    color: '#333',
    marginBottom: 10,
  },
  pdfSubtext: {
    fontSize: 16,
    color: '#666',
    textAlign: 'center',
    marginBottom: 20,
  },
  buttonContainer: {
    width: '100%',
    gap: 12,
  },
  primaryButton: {
    backgroundColor: '#007AFF',
    paddingHorizontal: 30,
    paddingVertical: 15,
    borderRadius: 8,
  },
  primaryButtonText: {
    color: 'white',
    fontSize: 18,
    fontWeight: '600',
    textAlign: 'center',
  },
  secondaryButton: {
    backgroundColor: 'transparent',
    borderWidth: 1,
    borderColor: '#007AFF',
    paddingHorizontal: 30,
    paddingVertical: 15,
    borderRadius: 8,
  },
  secondaryButtonText: {
    color: '#007AFF',
    fontSize: 16,
    fontWeight: '600',
    textAlign: 'center',
  },
  pdfHint: {
    fontSize: 14,
    color: '#888',
    textAlign: 'center',
    paddingHorizontal: 20,
    lineHeight: 20,
  },
});
